import {
  FormEvent,
  forwardRef,
  ForwardRefRenderFunction,
  InputHTMLAttributes,
  useCallback,
  useRef,
} from "react";
import { FieldError } from "react-hook-form";
import { cep, cpf, currency, date } from "./masks";

import 'react-phone-input-2/lib/style.css'

import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input as FormInput,
  FormPhoneInput
} from "./styles";

type InputType =
  | "text"
  | "email"
  | "password"
  | "number"
  | "tel"
  | "url"
  | "date"
  | "time"
  | "datetime-local";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label?: string;
  error?: string | FieldError;
  showErrorMessage?: boolean;
  borderColor: string;
  textColor?: string;

  mask?: "cep" | "currency" | "cpf" | "phone" | "date";
  type?: InputType;
}

const InputBase: ForwardRefRenderFunction<HTMLInputElement, InputProps> = (
  { id, label, error, showErrorMessage = true, borderColor, textColor, mask = "", type = "text", ...rest },
  ref
) => {
  const phoneInputRef = useRef<{ input: HTMLInputElement }>(null);
  const { onChange, name } = rest;

  const handleKeyUp = useCallback(
    (e: FormEvent<HTMLInputElement>) => {
      if (mask === "cep") cep(e);
      if (mask === "currency") currency(e);
      if (mask === "cpf") cpf(e);
      if (mask === "date") date(e);
    },
    [mask]
  );

  const handlePhoneChange = useCallback((value: string) => {
    onChange?.({ target: { value, name } } as React.ChangeEvent<HTMLInputElement>);

    if (phoneInputRef.current?.input) {
      phoneInputRef.current.input.value = value;
    }
  }, [onChange, name]);

  return (
    <FormControl isInvalid={!!error}>
      {!!label && <FormLabel htmlFor={id} $textColor={textColor}>{label}</FormLabel>}

      {!mask ? (
        <FormInput
          id={id}
          ref={ref}
          type={type} 
          $bordercolor={borderColor}
          aria-invalid={!!error && showErrorMessage ? "true" : "false"}
          autoComplete={rest.autoComplete || "on"}
          {...rest}
        />
      ) : mask === 'phone' ? (
        <FormPhoneInput
          country={"br"}
          $bordercolor={borderColor}
          placeholder={rest.placeholder}
          aria-invalid={!!error && showErrorMessage ? "true" : "false"}
          isInvalid={!!error}
          onChange={handlePhoneChange}
          masks={{
            br: "(..) .....-....",}}
          inputProps={{
            id,
            name: 'phone',
            required: true,
            autoFocus: true,
            autoComplete: "tel",
            ref: phoneInputRef,
          }}
          //TODO no futuro enviar com o ddi, só retirar o disableCountryCode e disableCountryGuess
          dropdownStyle={{
            color: textColor,
          }}
          disableCountryGuess={true}
          disableCountryCode={true}
          value={rest.value as string} 
        />
      ) : (
        <FormInput
          id={id}
          ref={ref}
          type={type} 
          $bordercolor={borderColor}
          aria-invalid={!!error && showErrorMessage ? "true" : "false"}
          onKeyUp={handleKeyUp}
          autoComplete={rest.autoComplete || "on"}
          {...rest}
        />
      )}

      {!!error && showErrorMessage && (
        <FormErrorMessage data-testid="error-message">
          {typeof error === 'string' ? error : error.message}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

export const Input = forwardRef(InputBase);
